import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { convertHeicToJpeg, isHeicFile } from "@/lib/utils/heic-converter";

// Mirrors the legacy composer's attachment cap (256MB) and copy.
export const MAX_MEDIA_SIZE_BYTES = 256 * 1000 * 1000;
const MEDIA_TOO_LARGE_MESSAGE = "File size should be less than 256MB.";
const MEDIA_CONVERT_ERROR_MESSAGE = "Couldn't process this image. Try another.";

export interface UseAttentionMediaStagingResult {
  imageFile: File | null;
  imagePreview: string | null;
  converting: boolean;
  mediaError: string | null;
  selectedGif: string | null;
  selectImageFile: (file: File | null | undefined) => void;
  clearImage: () => void;
  selectGif: (url: string) => void;
  clearGif: () => void;
  imageKind: "image" | "video" | null;
}

export function useAttentionMediaStaging(): UseAttentionMediaStagingResult {
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [mediaError, setMediaError] = useState<string | null>(null);
  const [converting, setConverting] = useState(false);
  const [selectedGif, setSelectedGif] = useState<string | null>(null);
  const imagePreviewRef = useRef<string | null>(null);
  // Bumped on every select/clear so a slow HEIC conversion can't clobber a newer pick.
  const selectionTokenRef = useRef(0);

  useEffect(
    () => () => {
      if (imagePreviewRef.current !== null) {
        URL.revokeObjectURL(imagePreviewRef.current);
        imagePreviewRef.current = null;
      }
    },
    [],
  );

  const revokeImagePreview = useCallback(() => {
    if (imagePreviewRef.current !== null) {
      URL.revokeObjectURL(imagePreviewRef.current);
      imagePreviewRef.current = null;
    }
  }, []);

  const clearImage = useCallback(() => {
    selectionTokenRef.current += 1;
    revokeImagePreview();
    setConverting(false);
    setMediaError(null);
    setImagePreview(null);
    setImageFile(null);
  }, [revokeImagePreview]);

  const clearGif = useCallback(() => setSelectedGif(null), []);

  const stageMedia = useCallback(
    (file: File) => {
      revokeImagePreview();
      const url = URL.createObjectURL(file);
      imagePreviewRef.current = url;
      setMediaError(null);
      setImagePreview(url);
      setImageFile(file);
    },
    [revokeImagePreview],
  );

  const selectImageFile = useCallback(
    (file: File | null | undefined) => {
      if (!file) return;
      const heic = isHeicFile(file);
      const isMedia =
        heic ||
        file.type.startsWith("image/") ||
        file.type.startsWith("video/");
      if (!isMedia) return;
      if (file.size > MAX_MEDIA_SIZE_BYTES) {
        setMediaError(MEDIA_TOO_LARGE_MESSAGE);
        return;
      }
      const token = ++selectionTokenRef.current;
      if (!heic) {
        setConverting(false);
        stageMedia(file);
        return;
      }
      revokeImagePreview();
      setImagePreview(null);
      setImageFile(null);
      setMediaError(null);
      setConverting(true);
      void convertHeicToJpeg(file)
        .then((jpeg) => {
          if (selectionTokenRef.current !== token) return;
          setConverting(false);
          stageMedia(jpeg);
        })
        .catch(() => {
          if (selectionTokenRef.current !== token) return;
          setConverting(false);
          setMediaError(MEDIA_CONVERT_ERROR_MESSAGE);
        });
    },
    [revokeImagePreview, stageMedia],
  );

  const selectGif = useCallback((url: string) => setSelectedGif(url), []);

  const imageKind = useMemo<"image" | "video" | null>(() => {
    if (!imageFile) return null;
    return imageFile.type.startsWith("video/") ? "video" : "image";
  }, [imageFile]);

  return {
    imageFile,
    imagePreview,
    converting,
    mediaError,
    selectedGif,
    selectImageFile,
    clearImage,
    selectGif,
    clearGif,
    imageKind,
  };
}
